import { FlexColumn, FlexRow, LabeledTextArea, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { FileRow } from 'baseui/file-uploader';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createFleetAssetDocument, updateFleetAssetDocuments } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import {
  FleetAsset,
  FleetAssetDocumentDataRequest,
  FleetAssetDocumentList,
  FleetAssetDocumentType,
} from '../../../types/fleet';
import DocumentUploader from '../../shared/DocumentUploader';
import LocalizedDatePicker from '../../shared/LocalizedDatePicker';

type Props = {
  asset: FleetAsset;
  selectedDocument: FleetAssetDocumentType;
  documents?: FleetAssetDocumentList;
  onCancel: () => void;
  afterSave: () => void;
};

export function FleetAssetDocumentForm({ asset, selectedDocument, onCancel, documents, afterSave }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const documentData = documents?.[selectedDocument];
  const [editingDocument, setEditingDocument] = useState<FleetAssetDocumentDataRequest>({
    document: '',
    comments: '',
    expirationDate: undefined,
    fileRow: undefined,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (documentData) {
      setEditingDocument({
        comments: documentData.comments,
        document: documentData.document,
        expirationDate: documentData.expirationDate,
        fileRow: undefined,
      });
    }
  }, [documentData]);

  const canSubmit = useMemo(() => {
    return (
      !!editingDocument.comments &&
      !!editingDocument.expirationDate &&
      (!!editingDocument.document || !!editingDocument.fileRow)
    );
  }, [editingDocument]);

  const onInputChange = (
    value: string | undefined | null | number | FileRow,
    field: keyof FleetAssetDocumentDataRequest,
  ) => {
    setEditingDocument((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    if (!apiParams?.clientId) return;
    startLoading();
    let fileKey = editingDocument.document;
    if (editingDocument.fileRow) {
      const { error, path } = await createFleetAssetDocument({
        assetId: asset._id,
        clientId: apiParams.clientId,
        subClientId: apiParams?.subClientId,
        file: editingDocument.fileRow.file,
      });
      if (path && !error) {
        fileKey = path;
      } else if (error) {
        showFailFeedback(error);
        return;
      }
    }
    const { fileRow: file, ...documentData } = editingDocument;
    const { id, error } = await updateFleetAssetDocuments({
      assetId: asset._id,
      clientId: apiParams.clientId,
      subClientId: apiParams?.subClientId,
      documents: {
        ...documents,
        [selectedDocument]: {
          ...documentData,
          document: fileKey,
          expirationDate: documentData.expirationDate ?? 0,
        },
      },
    });
    if (!error && id) {
      afterSave();
      showPositiveFeedback(FEEDBACK.updated(FEEDBACK_PREFIXES.document));
    } else {
      showFailFeedback(FEEDBACK.failedUpdate(FEEDBACK_PREFIXES.document));
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <DocumentUploader
          file={editingDocument.fileRow}
          onFileChange={(file) => {
            onInputChange(file, 'fileRow');
          }}
        />
        <LabeledTextArea
          label="Comentarios"
          value={editingDocument.comments}
          onChangeValue={(value) => {
            onInputChange(value, 'comments');
          }}
          required
        />
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Fecha de vencimiento: *</LabelSmall>
          <LocalizedDatePicker
            value={editingDocument.expirationDate ? [new Date(editingDocument.expirationDate)] : []}
            onChange={({ date }) => {
              if (date && !Array.isArray(date)) {
                onInputChange(date.getTime(), 'expirationDate');
              }
            }}
            onClose={() => {
              if (editingDocument.expirationDate) {
                onInputChange(new Date(editingDocument.expirationDate).getTime(), 'expirationDate');
              } else {
                onInputChange(undefined, 'expirationDate');
              }
            }}
            size="compact"
          />
        </FlexColumn>
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {asset?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}

export default FleetAssetDocumentForm;
